import { useCallback, useState } from "react";
import { useAppSelector } from "src/common/state/hooks";
import { readLocalStorage, writeLocalStorage } from "src/common/util/localStorage";
import { getTour, isAwaitStep } from "src/features/onboarding/registry";
import {
  selectActiveTour,
  selectOnboardingAudience,
} from "src/features/onboarding/state/onboardingSlice";
import type { SidebarItemDef, SidebarSectionId } from "./sidebarConfig";

const COLLAPSE_STORAGE_KEY = "wixar.sidebar.collapsed";

type CollapseState = Partial<Record<SidebarSectionId, boolean>>;

const readCollapseState = (): CollapseState => {
  const raw = readLocalStorage(COLLAPSE_STORAGE_KEY);
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const isStoredCollapsed = (state: CollapseState, id: SidebarSectionId) =>
  state[id] ?? (id === "parameters" || id === "favorites");

export const useSidebarCollapse = (
  visibleItems: SidebarItemDef[],
  dragForcedSection?: SidebarSectionId,
) => {
  const [collapseState, setCollapseState] = useState<CollapseState>(readCollapseState);
  const activeTour = useAppSelector(selectActiveTour);
  const audience = useAppSelector(selectOnboardingAudience);

  const currentStep = activeTour
    ? getTour(activeTour.id, audience)?.steps[activeTour.stepIndex]
    : undefined;
  const anchor = currentStep && !isAwaitStep(currentStep) ? currentStep.anchor : null;
  const forcedSection = anchor
    ? visibleItems.find((item) => item.dataTour === anchor)?.section
    : undefined;

  const isCollapsed = (id: SidebarSectionId) =>
    id !== forcedSection && id !== dragForcedSection && isStoredCollapsed(collapseState, id);

  const toggle = useCallback(
    (id: SidebarSectionId) => {
      const next = { ...collapseState, [id]: !isStoredCollapsed(collapseState, id) };
      writeLocalStorage(COLLAPSE_STORAGE_KEY, JSON.stringify(next));
      setCollapseState(next);
    },
    [collapseState],
  );

  const expand = useCallback((id: SidebarSectionId) => {
    setCollapseState((current) => {
      if (!isStoredCollapsed(current, id)) return current;
      const next = { ...current, [id]: false };
      writeLocalStorage(COLLAPSE_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  return { isCollapsed, toggle, expand };
};
